import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { T } from '@tolgee/react';
import DistributorBanner from '../../../components/DistributorBanner';

const LoginPage = () => {
  const { auth, login, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener el redirect de los query params (si existe)
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect') || '/products';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
  });
  
  // Efecto para redirigir cuando el usuario se ha autenticado exitosamente
  useEffect(() => {
    if (auth.initialized && auth.isAuthenticated) {
      navigate(redirectTo);
    }
  }, [auth.isAuthenticated, auth.initialized, navigate, redirectTo]);
  
  const validateForm = () => {
    let isValid = true;
    const errors = {
      email: '',
      password: '',
    };
    
    // Validar email
    if (!formData.email.trim()) {
      errors.email = 'El email es obligatorio';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no es válido';
      isValid = false;
    }
    
    // Validar contraseña
    if (!formData.password.trim()) {
      errors.password = 'La contraseña es obligatoria';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Limpiar error del campo
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
    
    // Limpiar error general
    if (auth.error) {
      clearError();
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await login(formData.email, formData.password);
      // No necesitamos redirigir aquí, el useEffect se encargará cuando auth.isAuthenticated cambie
    } catch (error) {
      console.error('Error en el formulario de login:', error);
    }
  };
  
  // Si ya está autenticado y la inicialización completó, mostrar un indicador de carga mientras redirige
  if (auth.initialized && auth.isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Iniciar Sesión | Jarad - Nutrición Premium para Mascotas</title>
        <meta name="description" content="Inicia sesión en tu cuenta de Jarad para acceder a productos premium de nutrición para mascotas y gestionar tus pedidos." />
      </Helmet>

      <div className="min-h-screen" style={{backgroundColor: '#f2ede4'}}>
        <DistributorBanner />
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Formulario - Lado izquierdo */}
          <motion.div 
            className="flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-md w-full space-y-8">
              <div className="text-center">
                <h1 className="text-4xl font-extrabold mb-2" style={{color: '#0d0d0d'}}>
                  <T keyName="auth.login.title">Acceso Distribuidores</T>
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                  <T keyName="auth.login.subtitle">Inicia sesión para acceder a tu área exclusiva de distribuidor</T>
                </p>
              </div>
              
              <form className="space-y-6" onSubmit={handleSubmit} data-testid="login-form">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.login.email">Correo electrónico</T>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                        formErrors.email
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                      placeholder="tu@email.com"
                      data-testid="email-input"
                    />
                    {formErrors.email && (
                      <p className="mt-2 text-sm text-red-600" data-testid="email-error">{formErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.login.password">Contraseña</T>
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                        formErrors.password
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                      placeholder="••••••••"
                      data-testid="password-input"
                    />
                    {formErrors.password && (
                      <p className="mt-2 text-sm text-red-600" data-testid="password-error">{formErrors.password}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                      style={{accentColor: '#f6cf02'}}
                      data-testid="remember-me-checkbox"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.login.remember_me">Recordarme</T>
                    </label>
                  </div>

                  <div className="text-sm">
                    <Link to="/forgot-password" className="font-medium hover:underline" style={{color: '#0d0d0d'}} data-testid="forgot-password-link">
                      <T keyName="auth.login.forgot_password">¿Olvidaste tu contraseña?</T>
                    </Link>
                  </div>
                </div>

                {auth.error && (
                  <motion.div
                    className="bg-red-50 border-2 border-red-200 rounded-2xl p-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="login-error"
                  >
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{auth.error}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={auth.isLoading}
                  className="w-full py-3 px-6 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg disabled:opacity-50"
                  style={{
                    backgroundColor: '#f6cf02',
                    color: '#0d0d0d',
                    borderColor: '#0d0d0d'
                  }}
                  whileHover={{ scale: auth.isLoading ? 1 : 1.02 }}
                  whileTap={{ scale: auth.isLoading ? 1 : 0.98 }}
                  onMouseEnter={(e) => {
                    if (!auth.isLoading) {
                      e.currentTarget.style.backgroundColor = '#0d0d0d';
                      e.currentTarget.style.color = '#f6cf02';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!auth.isLoading) {
                      e.currentTarget.style.backgroundColor = '#f6cf02';
                      e.currentTarget.style.color = '#0d0d0d';
                    }
                  }}
                  data-testid="login-button"
                >
                  {auth.isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 mr-3" style={{borderColor: '#0d0d0d'}}></div>
                      <T keyName="auth.login.logging_in">Iniciando sesión...</T>
                    </div>
                  ) : (
                    <T keyName="auth.login.login_button">Iniciar sesión</T>
                  )}
                </motion.button>

                <div className="text-center">
                  <p className="text-gray-600">
                    <T keyName="auth.login.no_access">¿Eres distribuidor y no tienes acceso?</T>{' '}
                    <Link to="/register" className="font-medium hover:underline" style={{color: '#0d0d0d'}} data-testid="register-link">
                      <T keyName="auth.login.request_access">Solicita tu alta aquí</T>
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Imagen y contenido - Lado derecho */}
          <motion.div 
            className="hidden lg:flex items-center justify-center bg-white relative overflow-hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative z-10 max-w-lg px-8 text-center">
              
              <h2 className="text-3xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                <T keyName="auth.login.private_area">Área de Acceso Privado</T>
              </h2>
              
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                <T keyName="auth.login.private_area_description">Esta área está diseñada especialmente para distribuidores mayoristas y tiendas especializadas interesados en comercializar los productos de Jarad Pet Food.</T>
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4 text-left">
                  <h3 className="font-bold mb-2" style={{color: '#0d0d0d'}}><T keyName="auth.login.how_to_access">¿Cómo acceder a la plataforma?</T></h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <T keyName="auth.login.access_description">Solicita el acceso rellenando el formulario de "Solicitar alta". Una vez verificada tu solicitud, te proporcionaremos las credenciales necesarias para disfrutar de todos los beneficios de este espacio exclusivo.</T>
                  </p>
                </div>
                
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{backgroundColor: '#f6cf02'}}></div>
                  <p className="font-medium" style={{color: '#0d0d0d'}}><T keyName="auth.login.wholesale_prices">Precios Mayoristas</T></p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{backgroundColor: '#f6cf02'}}></div>
                  <p className="font-medium" style={{color: '#0d0d0d'}}><T keyName="auth.login.technical_advice">Asesoramiento Técnico</T></p>
                </div>
              </div>
            </div>
            
            {/* Elementos decorativos */}
            <motion.div 
              className="absolute top-10 right-10 w-20 h-20 rounded-full opacity-20"
              style={{backgroundColor: '#f6cf02'}}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute bottom-10 left-10 w-16 h-16 rounded-full opacity-15"
              style={{backgroundColor: '#f6cf02'}}
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.15, 0.25, 0.15]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default LoginPage; 